import { appPaths } from '@/app/router/paths.js';
import type { FormDrawerKey, FormDrawerParams } from '@/app/surfaces/form-drawer-registry.js';
import type { QuickPanelKey, QuickPanelParams } from '@/app/surfaces/quick-panel-registry.js';
import {
  resolveRegisterPartyTitle,
  resolveRegisterPartyTrail,
  type PartyEntryContext,
} from '@/features/customer/party-entry-context.js';
import type { SurfaceMode } from '@/shared/surfaces/surface-mode.js';

export type { SurfaceMode };
export type ScreenParams = Record<string, unknown>;

export interface ScreenRegistryEntry<TParams extends ScreenParams = ScreenParams> {
  /** Surfaces supported by this screen key. */
  supports: readonly SurfaceMode[];
  /** Route target for page mode. Required when supports includes "page". */
  toPath?: (params: TParams) => string;
  /** Drawer mapping for drawer mode. Required when supports includes "drawer". */
  drawer?: {
    key: FormDrawerKey;
    mapParams?: (params: TParams) => FormDrawerParams;
  };
  /** Quick panel mapping for quickPanel mode. Required when supports includes "quickPanel". */
  quickPanel?: {
    key: QuickPanelKey;
    mapParams?: (params: TParams) => QuickPanelParams;
  };
}

const screenRegistryDefinition = {
  home: {
    supports: ['page'],
    toPath: () => appPaths.home,
  },
  'design-system': {
    supports: ['page'],
    toPath: () => appPaths.harnessDesignSystem,
  },
  'appointments.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.appointments,
    quickPanel: { key: 'appointment-list' },
  },
  'notification.campaigns': {
    supports: ['page'],
    toPath: () => appPaths.notificationCampaigns,
  },
  'notification.campaigns.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'place-notification-campaign-form',
      mapParams: (params) => {
        const campaignId =
          typeof params['campaignId'] === 'string' ? params['campaignId'] : undefined;
        const isEdit = campaignId !== undefined && campaignId.length > 0;
        return {
          campaignId,
          title: isEdit ? 'Editar campanha' : 'Nova campanha',
          trail: ['Campanhas', isEdit ? 'Editar campanha' : 'Nova campanha'],
        };
      },
    },
  },
  'customers.list': {
    supports: ['page', 'drawer', 'quickPanel'],
    toPath: () => appPaths.customers,
    drawer: { key: 'customers-list' },
    quickPanel: { key: 'customer-list' },
  },
  'customers.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: { key: 'register-party' },
    quickPanel: {
      key: 'customers-register',
      mapParams: (params) => {
        const entryContext = (params['entryContext'] ?? 'CUSTOMER') as PartyEntryContext;
        const title = resolveRegisterPartyTitle(entryContext);
        return {
          title,
          trail: [...resolveRegisterPartyTrail(entryContext)],
          entryContext,
          ...(typeof params['initialDocument'] === 'string' && params['initialDocument'].length > 0
            ? { initialDocument: params['initialDocument'] }
            : {}),
          onPartyCreated: params['onPartyCreated'],
          onDismissFlow: params['onDismissFlow'],
        };
      },
    },
  },
  'parties.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: {
      key: 'register-party',
      mapParams: (params) => ({
        entryContext: params['entryContext'],
      }),
    },
    quickPanel: {
      key: 'register-party',
      mapParams: (params) => {
        const entryContext = params['entryContext'] as PartyEntryContext;
        const title = resolveRegisterPartyTitle(entryContext);
        return {
          title,
          trail: [...resolveRegisterPartyTrail(entryContext)],
          entryContext,
          onPartyCreated: params['onPartyCreated'],
          onDismissFlow: params['onDismissFlow'],
        };
      },
    },
  },
  'customers.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.customersDetail(p['partyId'] as string),
  },
  'suppliers.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.suppliers,
    quickPanel: { key: 'suppliers-list' },
  },
  'suppliers.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'suppliers-register',
      mapParams: (params) => ({
        title: 'Novo fornecedor',
        trail: ['Fornecedores', 'Novo fornecedor'],
        partyId: params['partyId'],
        prefill: params['prefill'],
        onSupplierRoleCreated: params['onSupplierRoleCreated'],
        onDismissFlow: params['onDismissFlow'],
      }),
    },
  },
  'suppliers.detail': {
    supports: ['page', 'quickPanel'],
    toPath: (p) => appPaths.suppliersDetail(p['supplierRoleId'] as string),
    quickPanel: { key: 'supplier-detail' },
  },
  'catalog.services.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.catalogServices,
    quickPanel: { key: 'catalog-services-list' },
  },
  'catalog.services.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: { key: 'register-service' },
    quickPanel: {
      key: 'register-service',
      mapParams: (params) => ({
        title: 'Novo serviço',
        trail: ['Serviços', 'Novo serviço'],
        onSuccessServiceId: params['onSuccessServiceId'],
      }),
    },
  },
  'catalog.services.detail': {
    supports: ['page'],
    toPath: (params) => appPaths.catalogServiceDetail(params['serviceId'] as string),
  },
  'catalog.services.fiscal-profile.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'catalog-service-tax-profile-form' },
  },
  'catalog.services.fitment.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-service-fitment-form',
      mapParams: (params) => ({
        serviceId: params['serviceId'],
        applicationId: params['applicationId'],
        title:
          typeof params['applicationId'] === 'string' && params['applicationId'].length > 0
            ? 'Editar aplicação veicular'
            : 'Nova aplicação veicular',
      }),
    },
  },
  'catalog.products.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.catalogProducts,
    quickPanel: { key: 'catalog-products-list' },
  },
  'catalog.products.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: { key: 'register-product' },
    quickPanel: {
      key: 'register-product',
      mapParams: (params) => ({
        title: 'Novo produto',
        trail: ['Produtos', 'Novo produto'],
        onSuccessProductId: params['onSuccessProductId'],
        cloneFromProductId: params['cloneFromProductId'],
      }),
    },
  },
  'catalog.products.detail': {
    supports: ['page'],
    toPath: (params) => appPaths.catalogProductDetail(params['productId'] as string),
  },
  'catalog.products.fiscal-profile.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'catalog-product-tax-profile-form' },
  },
  'catalog.products.fitment.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-fitment-form',
      mapParams: (params) => ({
        productId: params['productId'],
        applicationId: params['applicationId'],
        title:
          typeof params['applicationId'] === 'string' && params['applicationId'].length > 0
            ? 'Editar aplicação veicular'
            : 'Nova aplicação veicular',
      }),
    },
  },
  'catalog.products.applicable': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-products-applicable',
      mapParams: (params) => ({
        title: 'Produtos aplicáveis ao veículo',
        vehicleModelId: params['vehicleModelId'],
        manufactureYear: params['manufactureYear'],
        vehicleEngineId: params['vehicleEngineId'],
        placeId: params['placeId'],
        vehicleModelLabel: params['vehicleModelLabel'],
        onSelectProduct: params['onSelectProduct'],
      }),
    },
  },
  'catalog.services.applicable': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-services-applicable',
      mapParams: (params) => ({
        title: 'Serviços aplicáveis ao veículo',
        vehicleModelId: params['vehicleModelId'],
        manufactureYear: params['manufactureYear'],
        vehicleEngineId: params['vehicleEngineId'],
        vehicleModelLabel: params['vehicleModelLabel'],
        onSelectService: params['onSelectService'],
      }),
    },
  },
  'catalog.taxonomies.list': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-taxonomy-list',
      mapParams: (params) => ({
        entity: params['entity'],
        pickerMode: params['pickerMode'],
        autoStackCreate: params['autoStackCreate'],
        title: params['title'],
        onSelectTaxonomy: params['onSelectTaxonomy'],
        onTaxonomyCreated: params['onTaxonomyCreated'],
      }),
    },
  },
  'catalog.kits.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.catalogKits,
    quickPanel: { key: 'catalog-kits-list' },
  },
  'catalog.useful-life.list': {
    supports: ['page'],
    toPath: () => appPaths.catalogUsefulLife,
  },
  'catalog.kits.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: { key: 'register-kit' },
    quickPanel: {
      key: 'register-kit',
      mapParams: (params) => ({
        title: 'Novo kit',
        trail: ['Kits', 'Novo kit'],
        onSuccessKitId: params['onSuccessKitId'],
      }),
    },
  },
  'catalog.kits.detail': {
    supports: ['page'],
    toPath: (params) => appPaths.catalogKitDetail(params['kitId'] as string),
  },
  'catalog.kits.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'kit-edit',
      mapParams: (params) => ({
        kitId: params['kitId'],
      }),
    },
  },
  'catalog.kits.composition.revise': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'kit-composition-revise',
      mapParams: (params) => ({
        kitId: params['kitId'],
      }),
    },
  },
  'catalog.kits.status.change': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'change-kit-status',
      mapParams: (params) => ({
        kitId: params['kitId'],
        currentStatus: params['currentStatus'],
      }),
    },
  },
  'catalog.revision-plans.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.catalogRevisionPlans,
    quickPanel: { key: 'catalog-revision-plans-list' },
  },
  'catalog.revision-plans.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'register-revision-plan',
      mapParams: (params) => ({
        title: 'Novo plano de revisão',
        trail: ['Planos de revisão', 'Novo plano'],
        onSuccessRevisionPlanId: params['onSuccessRevisionPlanId'],
      }),
    },
  },
  'catalog.revision-plans.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'edit-revision-plan',
      mapParams: (params) => ({
        revisionPlanId: params['revisionPlanId'],
        title: 'Editar plano de revisão',
      }),
    },
  },
  'catalog.maintenance-checklists.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.catalogMaintenanceChecklists,
    quickPanel: { key: 'catalog-maintenance-checklists-list' },
  },
  'catalog.maintenance-checklists.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'register-maintenance-checklist',
      mapParams: (params) => ({
        title: 'Novo check-list de manutenção',
        trail: ['Check-lists de manutenção', 'Novo check-list'],
        onSuccessMaintenanceChecklistId: params['onSuccessMaintenanceChecklistId'],
      }),
    },
  },
  'catalog.maintenance-checklists.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'edit-maintenance-checklist',
      mapParams: (params) => ({
        maintenanceChecklistId: params['maintenanceChecklistId'],
        title: 'Editar check-list de manutenção',
      }),
    },
  },
  'catalog.price-policy.default': {
    supports: ['page'],
    toPath: () => appPaths.catalogPricePolicy,
  },
  'catalog.price-policy.default.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-price-policy-form',
      mapParams: (params) => ({
        title: params['title'] ?? 'Cadastrar política',
      }),
    },
  },
  'catalog.price-simulator.list': {
    supports: ['page'],
    toPath: () => appPaths.catalogPriceSimulator,
  },
  'catalog.price-promotions.list': {
    supports: ['page'],
    toPath: () => appPaths.catalogPricePromotions,
  },
  'catalog.price-promotions.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-price-promotions-form',
      mapParams: (params) => ({
        title: params['title'] ?? 'Promoção de preço',
        promotion: params['promotion'] ?? null,
      }),
    },
  },
  'catalog.price-exception.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-price-exception-form',
      mapParams: (params) => ({
        title: params['title'] ?? 'Exceção de preço',
        tenantProductId: params['tenantProductId'],
        sku: params['sku'],
        name: params['name'],
        brandId: params['brandId'],
        brandName: params['brandName'],
        categoryId: params['categoryId'],
        categoryName: params['categoryName'],
        exception: params['exception'],
      }),
    },
  },
  'vehicles.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.vehicles,
    quickPanel: {
      key: 'party-vehicles-list',
      mapParams: (params) => ({
        partyId: params['partyId'],
        partyDisplayName: params['partyDisplayName'],
        autoStackCreate: params['autoStackCreate'],
        initialCustomerVehicleId: params['initialCustomerVehicleId'],
        title: params['title'],
        onSelectVehicle: params['onSelectVehicle'],
        onVehicleCreated: params['onVehicleCreated'],
      }),
    },
  },
  'vehicles.register': {
    supports: ['quickPanel', 'drawer'],
    drawer: { key: 'register-vehicle' },
    quickPanel: {
      key: 'register-vehicle',
      mapParams: (params) => ({
        partyId: params['partyId'],
        partyDisplayName: params['partyDisplayName'],
        title: params['title'] ?? 'Novo veículo',
        trail: ['Veículos', (params['title'] as string | undefined) ?? 'Novo veículo'],
        onVehicleCreated: params['onVehicleCreated'],
        registerLocal: params['registerLocal'],
      }),
    },
  },
  'vehicles.brands.list': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'vehicle-brands-list',
      mapParams: (params) => ({
        autoStackCreate: params['autoStackCreate'],
        title: params['title'] ?? 'Marcas',
        onSelectBrand: params['onSelectBrand'],
        onBrandCreated: params['onBrandCreated'],
      }),
    },
  },
  'vehicles.models.list': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'vehicle-models-list',
      mapParams: (params) => ({
        brandId: params['brandId'],
        autoStackCreate: params['autoStackCreate'],
        title: params['title'] ?? 'Modelos',
        onSelectModel: params['onSelectModel'],
        onModelCreated: params['onModelCreated'],
      }),
    },
  },
  'vehicles.detail': {
    supports: ['page', 'quickPanel'],
    toPath: (p) => appPaths.vehicleDetail(p['customerVehicleId'] as string),
    quickPanel: {
      key: 'vehicle-detail',
      mapParams: (params) => ({
        customerVehicleId: params['customerVehicleId'],
        partyId: params['partyId'],
        initialTab: params['initialTab'],
      }),
    },
  },
  'platform.organizations.list': {
    supports: ['page'],
    toPath: () => appPaths.adminOrganizations,
  },
  'platform.organizations.provision': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organizations-provision',
      mapParams: () => ({
        title: 'Nova organização',
        trail: ['Organizações', 'Nova organização'],
      }),
    },
  },
  'platform.organizations.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.adminOrganizationDetail(p['organizationId'] as string),
  },
  'platform.organizations.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'edit-organization',
      mapParams: (params) => ({
        organizationId: params['organizationId'],
        title: 'Editar organização',
        trail: ['Organização', 'Editar'],
      }),
    },
  },
  'platform.organizations.join-network': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-join-network',
      mapParams: (params) => ({
        organizationId: params['organizationId'],
        title: 'Vincular à rede',
        trail: ['Organização', 'Vincular à rede'],
      }),
    },
  },
  'platform.organizations.resend-bootstrap': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-resend-bootstrap',
      mapParams: (params) => ({
        organizationId: params['organizationId'],
        email: params['email'],
        fullName: params['fullName'],
        title: 'Reenviar convite',
        trail: ['Organização', 'Reenviar convite'],
      }),
    },
  },
  'platform.places.list': {
    supports: ['page'],
    toPath: () => appPaths.adminPlaces,
  },
  'platform.places.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.adminPlaceDetail(p['placeId'] as string),
  },
  'platform.places.provision': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'places-provision',
      mapParams: (params) => ({
        organizationId: params['organizationId'],
        title: 'Nova oficina',
        trail: ['Oficinas', 'Nova oficina'],
      }),
    },
  },
  'platform.places.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'places-edit',
      mapParams: (params) => ({
        placeId: params['placeId'],
        title: 'Editar oficina',
        trail: ['Oficina', 'Editar'],
      }),
    },
  },
  'platform.networks.list': {
    supports: ['page'],
    toPath: () => appPaths.adminNetworks,
  },
  'platform.networks.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'networks-register',
      mapParams: () => ({
        title: 'Nova rede',
        trail: ['Redes', 'Nova rede'],
      }),
    },
  },
  'platform.networks.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.adminNetworkDetail(p['networkId'] as string),
  },
  'platform.networks.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'networks-edit',
      mapParams: (params) => ({
        networkId: params['networkId'],
        title: 'Editar rede',
        trail: ['Rede', 'Editar'],
      }),
    },
  },
  'platform.catalog-global.list': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogGlobal,
  },
  'platform.catalog-global.curation': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogGlobalCuration,
  },
  'platform.catalog-global.link-generic': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-link-generic',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        title: 'Vincular genérico',
        trail: ['Curadoria', 'Vincular genérico'],
      }),
    },
  },
  'platform.catalog-global.attributes': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-attributes',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        title: 'Atributos da peça',
        trail: ['Curadoria', 'Atributos'],
      }),
    },
  },
  'platform.catalog-global.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-direct-register',
      mapParams: () => ({
        title: 'Nova peça',
        trail: ['Base global', 'Nova peça'],
      }),
    },
  },
  'platform.catalog-global.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-direct-edit',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        title: 'Editar peça',
        trail: ['Base global', 'Editar'],
      }),
    },
  },
  'platform.catalog-global.applicability-create': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-applicability-register',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        title: 'Nova aplicação',
        trail: ['Base global', 'Aplicabilidade'],
      }),
    },
  },
  'platform.catalog-global.applicability-edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-applicability-edit',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        title: 'Editar aplicação',
        trail: ['Base global', 'Aplicabilidade'],
      }),
    },
  },
  'platform.catalog-global.applicability-archive': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-product-applicability-archive',
      mapParams: (params) => ({
        catalogProductId: params['catalogProductId'],
        applicationId: params['applicationId'],
        title: 'Remover aplicação',
        trail: ['Base global', 'Aplicabilidade'],
      }),
    },
  },
  'platform.catalog-taxonomy.list': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogTaxonomy,
  },
  'platform.catalog-taxonomy.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-taxonomy-register',
      mapParams: () => ({
        title: 'Novo nó',
        trail: ['Taxonomia', 'Novo nó'],
      }),
    },
  },
  'platform.catalog-taxonomy.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-taxonomy-edit',
      mapParams: (params) => ({
        categoryId: params['categoryId'],
        title: 'Editar nó',
        trail: ['Taxonomia', 'Editar'],
      }),
    },
  },
  'platform.catalog-taxonomy.move': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-taxonomy-move',
      mapParams: (params) => ({
        categoryId: params['categoryId'],
        title: 'Mover nó',
        trail: ['Taxonomia', 'Mover'],
      }),
    },
  },
  'platform.catalog-taxonomy.archive': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-taxonomy-archive',
      mapParams: (params) => ({
        categoryId: params['categoryId'],
        title: 'Arquivar nó',
        trail: ['Taxonomia', 'Arquivar'],
      }),
    },
  },
  'platform.catalog-generic.list': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogGeneric,
  },
  'platform.catalog-generic.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-register',
      mapParams: () => ({
        title: 'Novo genérico',
        trail: ['Genéricos', 'Novo genérico'],
      }),
    },
  },
  'platform.catalog-generic.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-edit',
      mapParams: (params) => ({
        genericProductId: params['genericProductId'],
        title: 'Editar genérico',
        trail: ['Genéricos', 'Editar'],
      }),
    },
  },
  'platform.catalog-generic.archive': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-archive',
      mapParams: (params) => ({
        genericProductId: params['genericProductId'],
        title: 'Arquivar genérico',
        trail: ['Genéricos', 'Arquivar'],
      }),
    },
  },
  'platform.catalog-generic-services.list': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogGenericServices,
  },
  'platform.catalog-generic-services.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-service-register',
      mapParams: () => ({
        title: 'Novo serviço genérico',
        trail: ['Serviços genéricos', 'Novo'],
      }),
    },
  },
  'platform.catalog-generic-services.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-service-edit',
      mapParams: (params) => ({
        genericServiceId: params['genericServiceId'],
        title: 'Editar serviço genérico',
        trail: ['Serviços genéricos', 'Editar'],
      }),
    },
  },
  'platform.catalog-generic-services.archive': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-generic-service-archive',
      mapParams: (params) => ({
        genericServiceId: params['genericServiceId'],
        title: 'Arquivar serviço genérico',
        trail: ['Serviços genéricos', 'Arquivar'],
      }),
    },
  },
  'platform.catalog-manufacturers.list': {
    supports: ['page'],
    toPath: () => appPaths.adminCatalogManufacturers,
  },
  'platform.catalog-manufacturers.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-manufacturer-register',
      mapParams: () => ({
        title: 'Novo fabricante',
        trail: ['Fabricantes', 'Novo fabricante'],
      }),
    },
  },
  'platform.catalog-manufacturers.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-manufacturer-edit',
      mapParams: (params) => ({
        manufacturerId: params['manufacturerId'],
        manufacturerName: params['manufacturerName'],
        title: 'Editar fabricante',
        trail: ['Fabricantes', 'Editar'],
      }),
    },
  },
  'platform.catalog-manufacturers.archive': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'catalog-manufacturer-archive',
      mapParams: (params) => ({
        manufacturerId: params['manufacturerId'],
        manufacturerName: params['manufacturerName'],
        title: 'Arquivar fabricante',
        trail: ['Fabricantes', 'Arquivar'],
      }),
    },
  },
  'platform.users.list': {
    supports: ['page'],
    toPath: () => appPaths.adminUsers,
  },
  'identity.organization-users.invite': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-access-invite',
      mapParams: (params) => {
        const isEdit = params['mode'] === 'edit';
        return {
          userAccessId: params['userAccessId'],
          email: params['email'],
          fullName: params['fullName'],
          scopeType: params['scopeType'],
          placeName: params['placeName'],
          profileName: params['profileName'],
          title: isEdit ? 'Editar acesso' : 'Novo usuário',
          trail: ['Usuários', isEdit ? 'Editar acesso' : 'Novo usuário'],
        };
      },
    },
  },
  'platform.users.invite': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-user-invite',
      mapParams: (params) => {
        const isEdit = params['mode'] === 'edit';
        return {
          organizationId: params['organizationId'],
          email: params['email'],
          fullName: params['fullName'],
          scopeType: params['scopeType'],
          placeName: params['placeName'],
          profileName: params['profileName'],
          mode: isEdit ? 'edit' : 'create',
          title: isEdit ? 'Editar usuário' : 'Novo usuário',
          trail: ['Usuários', isEdit ? 'Editar usuário' : 'Novo usuário'],
        };
      },
    },
  },
  'platform.profiles.list': {
    supports: ['page'],
    toPath: () => appPaths.adminProfiles,
  },
  'platform.profiles.register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'profiles-register',
      mapParams: () => ({
        title: 'Novo perfil',
        trail: ['Perfis', 'Novo perfil'],
      }),
    },
  },
  'platform.profiles.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.adminProfileTemplateDetail(p['profileTemplateId'] as string),
  },
  'platform.profiles.edit': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'profiles-edit',
      mapParams: (params) => ({
        profileTemplateId: params['profileTemplateId'],
        title: 'Editar perfil',
        trail: ['Perfil', 'Editar'],
      }),
    },
  },
  'inventory.stock-balances.list': {
    supports: ['page', 'quickPanel'],
    toPath: () => appPaths.inventoryStockBalances,
    quickPanel: { key: 'stock-balances-list' },
  },
  'inventory.stock-balance.detail': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'stock-balance-detail',
      mapParams: (params) => ({
        placeProductId: params['placeProductId'],
        title: params['title'] ?? 'Detalhe do estoque',
      }),
    },
  },
  'inventory.stock-movements.list': {
    supports: ['page'],
    toPath: () => appPaths.inventoryStockMovements,
    quickPanel: {
      key: 'stock-movement-detail',
      mapParams: (params) => ({
        movementId: params['movementId'],
        title: params['title'] ?? 'Movimentação',
      }),
    },
  },
  'inventory.stock-transfers.list': {
    supports: ['page'],
    toPath: () => appPaths.inventoryStockTransfers,
    quickPanel: {
      key: 'stock-transfer-detail',
      mapParams: (params) => ({
        stockTransferId: params['stockTransferId'],
        title: params['title'] ?? 'Transferência',
      }),
    },
  },
  'inventory.stock-inventories.list': {
    supports: ['page'],
    toPath: () => appPaths.inventoryStockInventories,
    quickPanel: {
      key: 'stock-inventory-detail',
      mapParams: (params) => ({
        stockInventoryId: params['stockInventoryId'],
        title: params['title'] ?? 'Inventário físico',
      }),
    },
  },
  'inventory.inbound-conferences.list': {
    supports: ['page'],
    toPath: () => appPaths.inventoryInboundConferences,
  },
  'inventory.inbound-conference.detail': {
    supports: ['page'],
    toPath: (params) => appPaths.inventoryInboundConferenceDetail(params['conferenceId'] as string),
  },
  'inventory.inbound-conference.line': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'inventory.inbound-conference.line',
      mapParams: (params) => ({
        conferenceId: params['conferenceId'],
        lineId: params['lineId'],
        quantityBp: params['quantityBp'],
        productDescription: params['productDescription'],
        title: params['title'] ?? 'Conferir linha',
      }),
    },
  },
  'purchasing.purchase-quotes.list': {
    supports: ['page'],
    toPath: () => appPaths.purchasingQuotes,
  },
  'purchasing.purchase-quote.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.purchasingQuoteDetail(p['purchaseQuoteId'] as string),
  },
  'purchasing.purchase-orders.list': {
    supports: ['page'],
    toPath: () => appPaths.purchasingOrders,
  },
  'purchasing.purchase-order.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.purchasingOrderDetail(p['purchaseOrderId'] as string),
  },
  'service-order.check-in': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderCheckIn,
  },
  'service-order.check-in-new': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderCheckInNew,
  },
  'service-order.check-in-resume': {
    supports: ['page'],
    toPath: (p) => appPaths.serviceOrderCheckInResume(p['receptionId'] as string),
  },
  'service-order.quotes-list': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderQuotes,
  },
  'service-order.quote-detail': {
    supports: ['page'],
    toPath: (p) => appPaths.serviceOrderQuoteDetail(p['quoteId'] as string),
  },
  'service-order.service-orders-list': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrders,
  },
  'service-order.realized-margin': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderRealizedMargin,
  },
  'service-order.break-even': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderBreakEven,
  },
  'service-order.productivity': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderProductivity,
  },
  'service-order.service-order-detail': {
    supports: ['page'],
    toPath: (p) => appPaths.serviceOrderDetail(p['serviceOrderId'] as string),
  },
  'service-order.check-out-list': {
    supports: ['page'],
    toPath: () => appPaths.serviceOrderCheckOut,
  },
  'service-order.check-out-detail': {
    supports: ['page'],
    toPath: (p) => appPaths.serviceOrderCheckOutDetail(p['serviceOrderId'] as string),
  },
  'finance.caixa': {
    supports: ['page'],
    toPath: () => appPaths.caixa,
  },
  'finance.caixa.session.open': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'cash-session-open',
      mapParams: () => ({
        title: 'Abrir sessão de caixa',
        trail: ['Caixa', 'Abrir sessão'],
      }),
    },
  },
  'finance.caixa.session.sangria': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'cash-session-sangria',
      mapParams: (params) => ({
        sessionId: typeof params['sessionId'] === 'string' ? params['sessionId'] : undefined,
        title: 'Sangria',
        trail: ['Caixa', 'Sangria'],
      }),
    },
  },
  'finance.caixa.session.suprimento': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'cash-session-suprimento',
      mapParams: (params) => ({
        sessionId: typeof params['sessionId'] === 'string' ? params['sessionId'] : undefined,
        title: 'Suprimento',
        trail: ['Caixa', 'Suprimento'],
      }),
    },
  },
  'finance.caixa.session.close': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'cash-session-close',
      mapParams: (params) => ({
        sessionId: typeof params['sessionId'] === 'string' ? params['sessionId'] : undefined,
        derivedBalanceCents:
          typeof params['derivedBalanceCents'] === 'string'
            ? params['derivedBalanceCents']
            : undefined,
        title: 'Fechar sessão',
        trail: ['Caixa', 'Fechar sessão'],
      }),
    },
  },
  'finance.chart-of-accounts': {
    supports: ['page'],
    toPath: () => appPaths.financeChartOfAccounts,
  },
  'finance.inbound-charge-policy': {
    supports: ['page'],
    toPath: () => appPaths.financeInboundChargePolicy,
  },
  'finance.inbound-charge-policy.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'inbound-charge-policy-form',
      mapParams: () => ({
        title: 'Editar política de encargo',
        trail: ['Encargos de entrada', 'Editar política de encargo'],
      }),
    },
  },
  'finance.chart-of-accounts.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'chart-of-account-form',
      mapParams: (params) => {
        const accountId = typeof params['accountId'] === 'string' ? params['accountId'] : undefined;
        const isEdit = accountId !== undefined && accountId.length > 0;
        return {
          accountId,
          title: isEdit ? 'Editar conta' : 'Nova conta',
          trail: ['Plano de contas', isEdit ? 'Editar conta' : 'Nova conta'],
        };
      },
    },
  },
  'finance.accounts-payable': {
    supports: ['page'],
    toPath: () => appPaths.financeAccountsPayable,
  },
  'finance.accounts-payable.create': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'accounts-payable-create',
      mapParams: (params) => {
        const payableId = typeof params['payableId'] === 'string' ? params['payableId'] : undefined;
        const isEdit = payableId !== undefined;
        return {
          ...(payableId !== undefined ? { payableId } : {}),
          title: isEdit ? 'Editar título a pagar' : 'Novo título a pagar',
          trail: ['Contas a pagar', isEdit ? 'Editar título a pagar' : 'Novo título a pagar'],
        };
      },
    },
  },
  'finance.accounts-payable.detail': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'accounts-payable-detail',
      mapParams: (params) => {
        const payableId = typeof params['payableId'] === 'string' ? params['payableId'] : undefined;
        return {
          ...(payableId !== undefined ? { payableId } : {}),
          title: 'Título a pagar',
          trail: ['Contas a pagar', 'Título a pagar'],
        };
      },
    },
  },
  'finance.accounts-payable.recurring-detail': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'recurring-payable-detail',
      mapParams: (params) => {
        const scheduleId =
          typeof params['scheduleId'] === 'string' ? params['scheduleId'] : undefined;
        return {
          ...(scheduleId !== undefined ? { scheduleId } : {}),
          title: 'Conta fixa',
          trail: ['Contas a pagar', 'Conta fixa'],
        };
      },
    },
  },
  'finance.accounts-receivable': {
    supports: ['page'],
    toPath: (p) => {
      const customerPartyId = p['customerPartyId'];
      if (typeof customerPartyId === 'string' && customerPartyId.length > 0) {
        const params = new URLSearchParams({ customerPartyId });
        return `${appPaths.financeAccountsReceivable}?${params.toString()}`;
      }
      return appPaths.financeAccountsReceivable;
    },
  },
  'finance.accounts-receivable.create': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'accounts-receivable-create',
      mapParams: (params) => {
        const receivableId =
          typeof params['receivableId'] === 'string' ? params['receivableId'] : undefined;
        const isEdit = receivableId !== undefined;
        return {
          ...(isEdit ? { ...params, receivableId } : {}),
          title: isEdit ? 'Editar título a receber' : 'Novo título a receber',
          trail: ['Contas a receber', isEdit ? 'Editar título a receber' : 'Novo título a receber'],
        };
      },
    },
  },
  'finance.accounts-receivable.detail': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'accounts-receivable-detail',
      mapParams: (params) => {
        const receivableId =
          typeof params['receivableId'] === 'string' ? params['receivableId'] : undefined;
        return {
          ...params,
          ...(receivableId !== undefined ? { receivableId } : {}),
          title: 'Título a receber',
          trail: ['Contas a receber', 'Título a receber'],
        };
      },
    },
  },
  'finance.accounts-receivable.advance': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'receivable-advance-form',
      mapParams: (params) => ({
        receivableId:
          typeof params['receivableId'] === 'string' ? params['receivableId'] : undefined,
        title: 'Antecipar título',
        trail: ['Contas a receber', 'Antecipar título'],
      }),
    },
  },
  'finance.cash-flow': {
    supports: ['page'],
    toPath: () => appPaths.financeCashFlow,
  },
  'finance.income-statement': {
    supports: ['page'],
    toPath: () => appPaths.financeIncomeStatement,
  },
  'finance.payment-settings': {
    supports: ['page'],
    toPath: () => appPaths.financePaymentSettings,
  },
  'finance.collection-charges': {
    supports: ['page'],
    toPath: () => appPaths.financeCollectionCharges,
  },
  'finance.collection-charge.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'collection-charge-emit',
      mapParams: () => ({
        title: 'Emitir cobrança CNAB',
        trail: ['Cobranças', 'Emitir'],
      }),
    },
  },
  'finance.collection-charge.boleto': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'collection-charge-boleto',
      mapParams: (params) => {
        const chargeId = typeof params['chargeId'] === 'string' ? params['chargeId'] : undefined;
        const ourNumber = typeof params['ourNumber'] === 'string' ? params['ourNumber'] : undefined;
        const digitableLine =
          typeof params['digitableLine'] === 'string' ? params['digitableLine'] : undefined;
        return {
          ...(chargeId !== undefined ? { chargeId } : {}),
          ...(ourNumber !== undefined ? { ourNumber } : {}),
          ...(digitableLine !== undefined ? { digitableLine } : {}),
          title: 'Boleto',
          trail: ['Cobranças', 'Boleto'],
        };
      },
    },
  },
  'finance.remittance.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'collection-remittance',
      mapParams: () => ({
        title: 'Gerar remessa CNAB',
        trail: ['Cobranças', 'Remessa'],
      }),
    },
  },
  'finance.return-upload.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'collection-return-upload',
      mapParams: () => ({
        title: 'Importar retorno CNAB',
        trail: ['Cobranças', 'Retorno'],
      }),
    },
  },
  'finance.payment-remittance.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-remittance',
      mapParams: (params) => {
        const payableIds =
          typeof params['payableIds'] === 'string'
            ? params['payableIds']
            : Array.isArray(params['payableIds'])
              ? params['payableIds'].filter((id): id is string => typeof id === 'string').join(',')
              : undefined;
        return {
          ...(payableIds !== undefined && payableIds.length > 0 ? { payableIds } : {}),
          title: 'Gerar remessa de pagamento',
          trail: ['Contas a pagar', 'Remessa'],
        };
      },
    },
  },
  'finance.payment-return-upload.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-return-upload',
      mapParams: () => ({
        title: 'Importar retorno de pagamento',
        trail: ['Contas a pagar', 'Retorno'],
      }),
    },
  },
  'finance.bank-reconciliation': {
    supports: ['page'],
    toPath: () => appPaths.financeBankReconciliation,
  },
  'finance.bank-statement.import': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'bank-statement-import',
      mapParams: () => ({
        title: 'Importar OFX',
        trail: ['Conciliação', 'Importar'],
      }),
    },
  },
  'finance.bank-statement-line.match': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'bank-statement-line-match',
      mapParams: (params) => ({
        lineId: typeof params['lineId'] === 'string' ? params['lineId'] : undefined,
        title: 'Conciliar linha',
        trail: ['Conciliação', 'Match'],
      }),
    },
  },
  'finance.payment-settings.policy.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-settings-policy-form',
      mapParams: () => ({
        title: 'Gate de liquidação',
        trail: ['Pagamentos', 'Gate de liquidação'],
      }),
    },
  },
  'finance.payment-settings.charges.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-method-charges-form',
      mapParams: () => ({
        title: 'Editar encargos',
        trail: ['Pagamentos', 'Editar encargos'],
      }),
    },
  },
  'finance.payment-settings.card-fee-installments.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'card-fee-installments-form',
      mapParams: (params) => ({
        title: 'Quantidade de vezes',
        trail: ['Pagamentos', 'Quantidade de vezes'],
        taxaKey: params['taxaKey'],
        method: params['method'],
        installmentsJson: params['installmentsJson'],
      }),
    },
  },
  'finance.payment-settings.financial-accounts.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-method-financial-accounts-form',
      mapParams: () => ({
        title: 'Contas por meio',
        trail: ['Pagamentos', 'Contas por meio'],
      }),
    },
  },
  'finance.payment-settings.account-bank.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'payment-account-bank-form',
      mapParams: (params) => {
        const accountId = typeof params['accountId'] === 'string' ? params['accountId'] : undefined;
        const accountName =
          typeof params['accountName'] === 'string' ? params['accountName'] : undefined;
        return {
          accountId,
          accountName,
          title: 'Dados bancários',
          trail: ['Pagamentos', 'Dados bancários'],
        };
      },
    },
  },
  'finance.payment-settings.bank-agreement.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'bank-agreement-form',
      mapParams: () => ({
        title: 'Novo convênio',
        trail: ['Pagamentos', 'Novo convênio'],
      }),
    },
  },
  'finance.payment-settings.financial-account.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'financial-account-form',
      mapParams: (params) => {
        const mode = params['mode'] === 'edit' ? 'edit' : 'create';
        const isEdit = mode === 'edit';
        return {
          mode,
          accountId: typeof params['accountId'] === 'string' ? params['accountId'] : undefined,
          name: typeof params['name'] === 'string' ? params['name'] : undefined,
          kind: typeof params['kind'] === 'string' ? params['kind'] : undefined,
          openingBalanceCents:
            typeof params['openingBalanceCents'] === 'string'
              ? params['openingBalanceCents']
              : undefined,
          title: isEdit ? 'Editar conta' : 'Nova conta financeira',
          trail: ['Pagamentos', isEdit ? 'Editar conta' : 'Nova conta'],
        };
      },
    },
  },
  'fiscal.parametrizacao': {
    supports: ['page'],
    toPath: () => appPaths.fiscalParametrizacao,
  },
  'fiscal.implantation-guide': {
    supports: ['page'],
    toPath: () => appPaths.fiscalImplantacao,
  },
  'fiscal.ncm.list': {
    supports: ['page'],
    toPath: () => appPaths.fiscalNcm,
  },
  'fiscal.ncm.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-ncm-form',
      mapParams: (params) => {
        const mode = params['mode'] === 'edit' ? 'edit' : 'create';
        const code = typeof params['code'] === 'string' ? params['code'] : undefined;
        const isEdit = mode === 'edit';
        return {
          mode,
          ...(code !== undefined ? { code } : {}),
          title: isEdit ? 'Editar NCM' : 'Novo NCM',
          trail: ['NCMs', isEdit ? 'Editar NCM' : 'Novo NCM'],
        };
      },
    },
  },
  'fiscal.documents.list': {
    supports: ['page'],
    toPath: () => appPaths.fiscalDocumentos,
  },
  'fiscal.inbound-register': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-inbound-register',
      mapParams: (params) => ({
        title: 'Dar entrada com XML',
        trail: ['Documentos fiscais', 'Dar entrada com XML'],
        initialXml: params['initialXml'],
        onRegistered: params['onRegistered'],
      }),
    },
  },
  'fiscal.inbound-xml-preview': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-inbound-xml-preview',
      mapParams: (params) => ({
        title: 'Nota completa (XML)',
        trail: ['Dar entrada com XML', 'Nota completa'],
        preview: params['preview'],
        xml: params['xml'],
      }),
    },
  },
  'fiscal.document.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.fiscalDocumentoDetail(p['documentId'] as string),
  },
  'fiscal.document.danfe': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-document-danfe',
      mapParams: (params) => {
        const placeId = typeof params['placeId'] === 'string' ? params['placeId'] : undefined;
        const documentId =
          typeof params['documentId'] === 'string' ? params['documentId'] : undefined;
        return {
          ...(placeId !== undefined ? { placeId } : {}),
          ...(documentId !== undefined ? { documentId } : {}),
          title: 'DANFE',
          trail: ['Documento fiscal', 'DANFE'],
        };
      },
    },
  },
  'fiscal.document.xml': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-document-xml',
      mapParams: (params) => {
        const placeId = typeof params['placeId'] === 'string' ? params['placeId'] : undefined;
        const documentId =
          typeof params['documentId'] === 'string' ? params['documentId'] : undefined;
        return {
          ...(placeId !== undefined ? { placeId } : {}),
          ...(documentId !== undefined ? { documentId } : {}),
          title: 'XML autorizado',
          trail: ['Documento fiscal', 'XML autorizado'],
        };
      },
    },
  },
  'fiscal.document.cancel': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-document-cancel',
      mapParams: (params) => {
        const documentId =
          typeof params['documentId'] === 'string' ? params['documentId'] : undefined;
        return {
          ...(documentId !== undefined ? { documentId } : {}),
          title: 'Cancelar NF',
          trail: ['Documento fiscal', 'Cancelar NF'],
        };
      },
    },
  },
  'fiscal.inbound-cancel': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-inbound-cancel',
      mapParams: (params) => {
        const documentId =
          typeof params['documentId'] === 'string' ? params['documentId'] : undefined;
        return {
          ...(documentId !== undefined ? { documentId } : {}),
          title: 'Cancelar entrada',
          trail: ['Documento fiscal', 'Cancelar entrada'],
        };
      },
    },
  },
  'fiscal.dfe-inbox.list': {
    supports: ['page'],
    toPath: () => appPaths.fiscalDfeInbox,
  },
  'fiscal.dfe-inbox.detail': {
    supports: ['page'],
    toPath: (p) => appPaths.fiscalDfeInboxDetail(p['documentId'] as string),
  },
  'fiscal.dfe-inbox.manifestation': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'fiscal-dfe-inbox-manifestation',
      mapParams: (params) => {
        const documentId =
          typeof params['documentId'] === 'string' ? params['documentId'] : undefined;
        return {
          ...(documentId !== undefined ? { documentId } : {}),
          title: 'Manifestar',
          trail: ['Caixa de entrada', 'Manifestar'],
        };
      },
    },
  },
  'identity.place-settings': {
    supports: ['page'],
    toPath: (params) => {
      const placeId = typeof params['placeId'] === 'string' ? params['placeId'] : '';
      return placeId.length > 0
        ? appPaths.organizationWorkshopDetail(placeId)
        : appPaths.organizationWorkshops;
    },
  },
  'identity.workshops.list': {
    supports: ['page'],
    toPath: () => appPaths.organizationWorkshops,
  },
  'organization-settings.hub': {
    supports: ['page'],
    toPath: () => appPaths.organizationSettings,
  },
  'organization-settings.inventory.policies.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-settings-inventory-policies-form',
      mapParams: () => ({
        title: 'Políticas de estoque',
        trail: ['Configurações da organização', 'Estoque', 'Editar'],
      }),
    },
  },
  'organization-settings.commission-policy.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'organization-settings-commission-policy-form',
      mapParams: () => ({
        title: 'Comissão da organização',
        trail: ['Configurações da organização', 'Comissão', 'Editar'],
      }),
    },
  },
  'identity.geo-city.list': {
    supports: ['page'],
    toPath: () => appPaths.geoCities,
  },
  'identity.geo-city.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-geo-city-form',
      mapParams: (params) => {
        const mode = params['mode'] === 'edit' ? 'edit' : 'create';
        const cityId = typeof params['cityId'] === 'string' ? params['cityId'] : undefined;
        const isEdit = mode === 'edit';
        return {
          mode,
          ...(cityId !== undefined ? { cityId } : {}),
          title: isEdit ? 'Editar cidade' : 'Nova cidade',
          trail: ['Cidades', isEdit ? 'Editar cidade' : 'Nova cidade'],
        };
      },
    },
  },
  'identity.place-settings.address.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-address-form' },
  },
  'identity.place-settings.agenda.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-agenda-form' },
  },
  'identity.place-settings.sector.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-sector-form' },
  },
  'identity.place-settings.workbay.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-workbay-form' },
  },
  'identity.place-settings.storage.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-storage-form' },
  },
  'identity.place-settings.delete': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-place-settings-delete',
      mapParams: (params) => {
        const kind = params['kind'];
        const title =
          kind === 'workbay'
            ? 'Excluir box'
            : kind === 'storage'
              ? 'Excluir armazém'
              : 'Excluir setor';
        return { ...params, title, trail: ['Configurações da unidade', title] };
      },
    },
  },
  'identity.place-settings.labor-rate.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-labor-rate-form' },
  },
  'identity.place-settings.commission-policy.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'identity-place-settings-commission-policy-form' },
  },
  'identity.place-settings.collaborator.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-place-settings-collaborator-form',
      mapParams: (params) => {
        const userId = typeof params['userId'] === 'string' ? params['userId'] : undefined;
        return {
          ...params,
          ...(userId !== undefined ? { userId } : {}),
          title: 'Classificação do colaborador',
          trail: ['Configurações da unidade', 'Colaboradores', 'Classificação'],
        };
      },
    },
  },
  'identity.place-settings.collaborator-commission.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-place-settings-collaborator-commission-form',
      mapParams: (params) => {
        const userId = typeof params['userId'] === 'string' ? params['userId'] : undefined;
        return {
          ...params,
          ...(userId !== undefined ? { userId } : {}),
          title: 'Comissão do colaborador',
          trail: ['Configurações da unidade', 'Colaboradores', 'Comissão'],
        };
      },
    },
  },
  'identity.place-settings.collaborator-work-hours.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-place-settings-collaborator-work-hours-form',
      mapParams: (params) => {
        const userId = typeof params['userId'] === 'string' ? params['userId'] : undefined;
        return {
          ...params,
          ...(userId !== undefined ? { userId } : {}),
          title: 'Jornada do colaborador',
          trail: ['Configurações da unidade', 'Colaboradores', 'Jornada'],
        };
      },
    },
  },
  'identity.place-settings.collaborator-wage.form': {
    supports: ['quickPanel'],
    quickPanel: {
      key: 'identity-place-settings-collaborator-wage-form',
      mapParams: (params) => {
        const userId = typeof params['userId'] === 'string' ? params['userId'] : undefined;
        return {
          ...params,
          ...(userId !== undefined ? { userId } : {}),
          title: 'Salário do colaborador',
          trail: ['Configurações da unidade', 'Colaboradores', 'Salário'],
        };
      },
    },
  },
  'fiscal.place-tax-profile.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-tax-profile-form' },
  },
  'fiscal.place-certificate.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-certificate-form' },
  },
  'fiscal.place-nfce-csc.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-nfce-csc-form' },
  },
  'fiscal.place-emission-settings': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-emission-settings' },
  },
  'fiscal.place-operation.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-operation-form' },
  },
  'fiscal.place-rule-set.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-rule-set-form' },
  },
  'fiscal.place-tax-profile.detail': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-tax-profile-detail' },
  },
  'fiscal.place-certificate.detail': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-certificate-detail' },
  },
  'fiscal.tenant-enabled-operation.detail': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-tenant-enabled-operation-detail' },
  },
  'fiscal.tenant-enabled-operation.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-tenant-enabled-operation-form' },
  },
  'fiscal.place-rule-set.detail': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-rule-set-detail' },
  },
  'fiscal.place-rule-set.editor': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-rule-set-editor' },
  },
  'fiscal.place-tax-rule.form': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-place-tax-rule-form' },
  },
  'fiscal.catalog-code.picker': {
    supports: ['quickPanel'],
    quickPanel: { key: 'fiscal-catalog-code-picker' },
  },
} as const satisfies Record<string, ScreenRegistryEntry>;

export type ScreenKey = keyof typeof screenRegistryDefinition;

/**
 * Global screen registry used by openScreen.
 * Keep react-router paths as source of truth for page openings.
 */
export const screenRegistry: Record<ScreenKey, ScreenRegistryEntry> = screenRegistryDefinition;
